import { c as _c } from "react/compiler-runtime";
import WorldMobileTitleArea from '@/app/[locale]/(worlds)/[roomId]/components/WorldMobileTitleArea';
import OptimizedImage from '@/components/OptimizedImage';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useMobilePlayCtaPlacementContext } from '@/context/MobilePlayCtaPlacementContext';
import { useUAContext } from '@/context/UAContext';
import { useMobilePlayMediaLink } from '@/hooks/useMobilePlayMediaLink';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
type TrailerCarouselImageOverlayProps = {
  url?: string;
  /**
   * Load the cover eagerly on mobile devices. Only set this while the background is a skeleton,
   * so the cover is the sole first-screen image and nothing competes with it for bandwidth.
   */
  eager?: boolean;
};
const TrailerCarouselImageOverlay = t0 => {
  const $ = _c(34);
  const {
    url,
    eager: t1
  } = t0;
  const eager = t1 === undefined ? false : t1;
  const {
    roomData
  } = useCheckEnterRoomContext();
  const {
    device,
    isMobileLandscape
  } = useUAContext();
  const {
    mediaStackBannerOffset
  } = useMobilePlayCtaPlacementContext();
  const playLink = useMobilePlayMediaLink();
  const isIconOnly = playLink?.isIconOnly ?? false;
  const isMobileDevice = device === "mobile_browser" || device === "mobile_app";
  const worldTitle = roomData?.title;
  const creatorName = roomData?.owner?.display_name;
  const creatorProfileHref = roomData?.owner?.community_user_id ? `/profile/${roomData.owner.community_user_id}` : undefined;
  let t2;
  if ($[0] !== eager || $[1] !== isIconOnly || $[2] !== isMobileDevice || $[3] !== isMobileLandscape || $[4] !== url) {
    t2 = url && !isMobileLandscape ? <div className={cn("relative overflow-hidden rounded-lg", isIconOnly ? "aspect-video w-60" : "h-[101px] w-[180px]")}><OptimizedImage src={url} alt="" fill={true} loading={eager && isMobileDevice ? "eager" : "lazy"} sizes={isIconOnly ? "240px" : "180px"} className="object-cover" /></div> : null;
    $[0] = eager;
    $[1] = isIconOnly;
    $[2] = isMobileDevice;
    $[3] = isMobileLandscape;
    $[4] = url;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const thumbnail = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="to-background absolute inset-0 bg-linear-to-b from-transparent lg:hidden" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== playLink) {
    t4 = playLink && <div aria-hidden={true} onClick={playLink.onPlay} data-bi-key="world_mobile_play" {...playLink.captureAttributes} className="absolute inset-0 cursor-pointer lg:hidden" />;
    $[7] = playLink;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isIconOnly || $[10] !== thumbnail) {
    t5 = isIconOnly && <div className="pointer-events-none absolute inset-0 flex items-center justify-center lg:hidden">{thumbnail}</div>;
    $[9] = isIconOnly;
    $[10] = thumbnail;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = playLink && "pointer-events-none";
  let t7;
  if ($[12] !== t6) {
    t7 = cn("absolute inset-x-0 bottom-[18px] flex flex-col items-center gap-2 lg:hidden", t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== mediaStackBannerOffset) {
    t8 = mediaStackBannerOffset > 0 ? {
      marginBottom: mediaStackBannerOffset
    } : undefined;
    $[14] = mediaStackBannerOffset;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = !isIconOnly && thumbnail;
  let t10;
  if ($[16] !== worldTitle) {
    t10 = worldTitle && <p className="max-w-[calc(100%-32px)] overflow-hidden text-[18px] leading-[26px] font-extrabold tracking-[0.1px] text-ellipsis whitespace-nowrap text-white">{worldTitle}</p>;
    $[16] = worldTitle;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== creatorName || $[19] !== creatorProfileHref) {
    t11 = creatorName && (creatorProfileHref ? <Link href={creatorProfileHref} className="text-vive-primary-30 pointer-events-auto text-[12px] leading-[18px] font-semibold tracking-[0.15px]">@{creatorName}</Link> : <p className="text-vive-primary-30 text-[12px] leading-[18px] font-semibold tracking-[0.15px]">@{creatorName}</p>);
    $[18] = creatorName;
    $[19] = creatorProfileHref;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== isMobileLandscape) {
    t12 = isMobileLandscape && <WorldMobileTitleArea />;
    $[21] = isMobileLandscape;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t13 = <div className={t7} style={t8}>{t9}{t10}{t11}{t12}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13 || $[31] !== t4 || $[32] !== t5) {
    t14 = <>{t3}{t4}{t5}{t13}</>;
    $[30] = t13;
    $[31] = t4;
    $[32] = t5;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
export default TrailerCarouselImageOverlay;